import {
  BulkSpecialtyGroupException,
  ExceptionsFilterRequestModel,
  ExportVolumeSpecialtyGroupExceptionsModel,
  CreateSpecialtyGroupExceptions,
  BulkExceptionEditModel,
  ExceptionsPaginationResult,
  BulkExceptionRationaleAnswersModel,
  BulkExceptionZeroPercentGainsProvidersModel,
  ExceptionRationaleAnswersModel,
  BulkExceptionCciioAnswersModel,
  ExceptionCciioAnswersModel,
  BulkFilterData,
  SpecialtyGroupException,
  SpecialtyGroupExceptionDisplay,
  CreateSpecialtyGroupException,
  SupplementaryDocument,
  UploadBulkExceptionsResponse,
  UpdateSpecialtyGroupException
} from "@app/exceptions/interfaces";
import {
  ExportCmsExceptionReportRequestModel,
  ExportCciioJustificationReportRequestModel
} from "@app/exports/exportModels";
import ServiceBase from "@app/shared/serviceBase";
import { clone } from "lodash";

export default class ExceptionsService extends ServiceBase {

  // GET

  /**
   * @returns a BulkFilterData object containing the project, specialty, service area, and tag
   * filter options for the exceptions Overview and Archived tabs in the Unified Exceptions View.
   */
  public async getFilterOptions(getArchived: boolean): Promise<BulkFilterData> {
    return await this.makeGet<BulkFilterData>(`specialtygroupexceptions/bulk-filters?getArchived=${getArchived}`);
  }

  /**
   * Retrieves all specialty group exceptions for a given project ID.
   * @param projectId
   */
  public async getSpecialtyGroupExceptionsByProjectId(projectId: number): Promise<SpecialtyGroupException[]> {
    return await this.makeGet<SpecialtyGroupException[]>(`specialtygroupexceptions/${projectId}/specialtygroupexceptions`);
  }

  /**
   * @param customProjectId integer representing the custom project id
   * @param exceptionId integer representing the exception id
   * @returns an ExceptionDetailslModel object containing the details of the exception of
   * retrieved from the given custom project id and exception id.
   */
  public async getSpecialtyGroupException(customProjectId: number, exceptionId: number): Promise<SpecialtyGroupException> {
    return await this.makeGet<SpecialtyGroupException>(`/specialtygroupexceptions/${customProjectId}/specialtygroupexceptions/${exceptionId}`);
  }

  /**
   * Retrieves all specialty group exceptions for a given project ID and run configuration ID.
   * @param projectId
   * @param runConfigurationId
   */
  public async getSpecialtyGroupExceptionsByRunConfigurationId(projectId: number, runConfigurationId: number): Promise<SpecialtyGroupException[]> {
    return await this.makeGet<SpecialtyGroupException[]>(`specialtygroupexceptions/${projectId}/runconfigurations/${runConfigurationId}/specialtygroupexceptions`);
  }

  /**
   * Retrieves all specialty group exception displays for a given project ID.
   * @param projectId
   */
  public async getSpecialtyGroupExceptionDisplaysByProjectId(projectId: number): Promise<SpecialtyGroupExceptionDisplay[]> {
    return await this.makeGet<SpecialtyGroupExceptionDisplay[]>(`specialtygroupexceptions/${projectId}/specialtygroupexceptiondisplays`);
  }

  /**
   * @param customProjectId integer representing the custom project id
   * @param regionSetId integer representing the region set id
   * @param specialtyGroupCode string representing the specialty group code
   * @returns a SpecialtyGroupExceptionDisplay array containing the history of specialty group exceptions
   * retrieved from the given custom project id, region set id, and specialty group code.
   */
  public async getSpecialtyGroupExceptionHistory(customProjectId: number, regionSetId: number, specialtyGroupCode: string): Promise<SpecialtyGroupExceptionDisplay[]> {
    if (!customProjectId || !regionSetId || !specialtyGroupCode) {
      return null;
    }
    return await this.makeGet<SpecialtyGroupExceptionDisplay[]>(`specialtygroupexceptions/${customProjectId}/regionSets/${regionSetId}/specialtygroupexceptionhistory`, { specialtyGroupCode });
  }

  /**
   * @param customProjectId integer representing the custom project id
   * @param regionSetSpecialtyGroupId integer representing the region set specialty group id
   * @returns a SpecialtyGroupExceptionDisplay array containing the history of specialty group exceptions
   * retrieved from the given custom project id and region set specialty group id.
   */
  public async getSpecialtyGroupExceptionHistoryByRegionSetSpecialtyGroupId(customProjectId: number, regionSetSpecialtyGroupId: number): Promise<SpecialtyGroupExceptionDisplay[]> {
    if (!customProjectId || !regionSetSpecialtyGroupId) {
      return null;
    }
    return await this.makeGet<SpecialtyGroupExceptionDisplay[]>(`specialtygroupexceptions/${customProjectId}/regionSetSpecialtyGroups/${regionSetSpecialtyGroupId}/specialtygroupexceptionhistory`);
  }

  /**
   * @param projectId integer representing the project id
   * @returns a SpecialtyGroupExceptionDisplay array containing the unprocessed specialty group exceptions
   * retrieved from the given project id.
   */
  public async getUnprocessedSpecialtyGroupExceptionsByProjectId(projectId: number): Promise<SpecialtyGroupExceptionDisplay[]> {
    return await this.makeGet<SpecialtyGroupExceptionDisplay[]>(`specialtygroupexceptions/${projectId}/unprocessed-specialty-group-exceptions`);
  }

  /**
   * @returns a boolean indicating whether there are unprocessed specialty group exceptions for the projects available to the user.
   */
  public async hasUnprocessedSpecialtyGroupExceptions(): Promise<boolean> {
    return await this.makeGet<boolean>(`specialtygroupexceptions/has-unprocessed`);
  }

  /**
   * @returns an ExportVolumeSpecialtyGroupExceptionsModel array containing the
   * volume exceptions data retrieved from the database.
   */
  public async getVolumeExceptionsDownloadReports(): Promise<ExportVolumeSpecialtyGroupExceptionsModel[]> {
    return await this.makeGet<ExportVolumeSpecialtyGroupExceptionsModel[]>('/specialtygroupexceptions/reports');
  }

  // POST

  /**
   * @param requestModel an instance of ExceptionsFilterRequestModel containing the
   * filter options to be used to retrieve the exceptions data.
   * @returns a ExceptionsPaginationResult<ExceptionsDataModel> object containing the standard bulk exceptions data
   * retrieved from the database using the provided filter options, plus summary statistics
   */
  public async getExceptionsData(requestModel: ExceptionsFilterRequestModel, signal?: AbortSignal): Promise<ExceptionsPaginationResult<BulkSpecialtyGroupException>> {
    /*We do a subtraction by 1 since the backend handles a zero based indexing,
    and clone the request to avoid updating the context value and getting it assigned to zero at some point */
    const clonedRequest = clone(requestModel);
    clonedRequest.pageIndex--;
    return await this.makePost<ExceptionsPaginationResult<BulkSpecialtyGroupException>>("/specialtygroupexceptions/bulk", clonedRequest, undefined, false, "application/json", true, undefined, signal);
  }

  public async getCCIIOExceptionStatus(customProjectIds: number[], abortSignal: AbortSignal): Promise<boolean> {
    return await this.makePost<boolean>(
      `/specialtygroupexceptions/cciio-exception-status`,
      customProjectIds,
      undefined,
      false,
      "application/json",
      true,
      undefined,
      abortSignal);
  }

  public static getCCIIOExceptionStatusQueryKey = "getCCIIOExceptionStatus";

  /**
   * @param model an instance of CreateExceptionsRequestModel containing the data to be used
   * to create multiple new exceptions.
   */
  public async createSpecialtyGroupExceptions(model: CreateSpecialtyGroupExceptions): Promise<void> {
    await this.makePost<void>('/specialtygroupexceptions', model);
  }

  /**
   *
   * @param model is an array of SpecialtyGroupException objects
   * @returns void
   */
  public async createSpecialtyGroupExceptionsFromList(model: SpecialtyGroupException[]): Promise<string[]> {
    return await this.makePost<string[]>('/specialtygroupexceptions/bulk/create', model);
  }

  /**
   * @param projectId the project ID
   * @param createSpecialtyGroupException an instance of CreateSpecialtyGroupException containing the data to be used
   * to create a new exception.
   * @returns a SpecialtyGroupException object representing the newly created exception.
   */
  public async createSpecialtyGroupException(projectId: number, createSpecialtyGroupException: CreateSpecialtyGroupException): Promise<SpecialtyGroupException> {
    return await this.makePost<SpecialtyGroupException>(`specialtygroupexceptions/${projectId}/specialtygroupexceptions`, createSpecialtyGroupException);
  }

  /**
   *
   * @param bulkExceptionsFile
   * @returns a UploadBulkExceptionResponse object
   */
  public async uploadBulkExceptionsFile(bulkExceptionsFile: FormData): Promise<UploadBulkExceptionsResponse> {
    if (!bulkExceptionsFile) {
      return null;
    }

    return await this.makePost<UploadBulkExceptionsResponse>(`specialtygroupexceptions/file/upload-bulk`, bulkExceptionsFile, undefined, true, false, false);
  }

  /**
   * Processes all unprocessed specialty group exceptions for a given project ID.
   * @param projectId
   */
  public async processUnprocessedSpecialtyGroupExceptions(projectId: number): Promise<void> {
    await this.makePost<void[]>(`specialtygroupexceptions/${projectId}/specialtygroupexceptions/Process-Unprocessed`);
  }

  /**
   * @param model an instance of ExportCmsExceptionReportRequestModel containing the
   * export options to be used to export the CMS exception report
   * @returns void
   */
  public async exportCmsExceptionReport(model: ExportCmsExceptionReportRequestModel): Promise<void> {
    await this.makePost<void>('/exports/cms-exception-report', model);
  }

  public async exportCciioJustificationReport(model: ExportCciioJustificationReportRequestModel): Promise<void> {
    await this.makePost<void>('/exports/cciio-justification', model);
  }

  public async uploadSupplementaryDocument(formData: FormData): Promise<number> {
    return await this.makePost<number>('/supplementarydocumentation', formData, undefined, true, false, false);
  }

  public async addSupplementaryDocumentsToExceptions(models: SupplementaryDocument[]): Promise<void> {
    await this.makePost<void>('/supplementarydocumentation/bulk', models);
  }

  public async getSpecialtyGroupExceptionsByIds(exceptionIds: number[], signal?: AbortSignal): Promise<SpecialtyGroupException[]> {
    return await this.makePost<SpecialtyGroupException[]>(
      '/specialtygroupexceptions/bulk/details',
      exceptionIds,
      undefined,
      false,
      "application/json",
      true,
      undefined,
      signal);
  }

  public static getSpecialtyGroupExceptionsByIdsQueryKey = "getSpecialtyGroupExceptionsByIds";

  public async getRationaleAnswersByExceptionIds(exceptionIds: number[]): Promise<ExceptionRationaleAnswersModel> {
    return await this.makePost<ExceptionRationaleAnswersModel>('/specialtygroupexceptions/rationale-answers', exceptionIds);
  }

  public async getZeroPercentGainsProvidersCommentByExceptionIds(exceptionIds: number[]): Promise<string> {
    return await this.makePost<string>('/specialtygroupexceptions/zero-percent-gains-providers-comment', exceptionIds);
  }

  // PATCH

  /**
   * Updates a specialty group exception for a given project ID.
   * @param projectId
   * @param updateSpecialtyGroupException
   */
  public async updateSpecialtyGroupException(projectId: number, updateSpecialtyGroupException: UpdateSpecialtyGroupException): Promise<SpecialtyGroupException> {
    return await this.makePatch<SpecialtyGroupException>(`specialtygroupexceptions/${projectId}/specialtygroupexceptions`, updateSpecialtyGroupException);
  }

  /**
   * @param model an instance of BulkExceptionEditModel containing a list of exception IDs and data to be used
   * to update the corresponding exceptions
   */
  public async bulkUpdateExceptionsByIds(model: BulkExceptionEditModel): Promise<void> {
    await this.makePatch<void>('/specialtygroupexceptions/bulk', model);
  }

  /**
   * @param exceptionIDs a list of SpecialtyGroupException IDs to be archived
   * @returns void
   */
  public async archiveExceptions(exceptionIDs: number[]): Promise<void> {
      return await this.makePatch<void>('/specialtygroupexceptions/archive/bulk', exceptionIDs);
  }

  /**
  * @param specialtyGroupExceptionIds a list of SpecialtyGroupException IDs to be expired
  * @returns void
  */
  public async expireSpecialtyGroupExceptions(specialtyGroupExceptionIds: number[]): Promise<void> {
    await this.makePatch<void>('/specialtygroupexceptions/expire/bulk', specialtyGroupExceptionIds);
  }

  public async bulkProcessUnprocessedSpecialtyGroupExceptions(): Promise<string> {
    return await this.makePost<string>(`specialtygroupexceptions/process-unprocessed/bulk`);
  }

  public async bulkUpdateSpecialtyGroupExceptionsRationales(model: BulkExceptionRationaleAnswersModel): Promise<void> {
    await this.makePatch<void>('/specialtygroupexceptions/bulk/rationale-answers', model);
  }

  public async bulkUpdateSpecialtyGroupZeroPercentGainsProviders(model: BulkExceptionZeroPercentGainsProvidersModel): Promise<void> {
    await this.makePatch<void>('/specialtygroupexceptions/bulk/zero-percent-gains-providers', model);
  }

  public async getCciioAnswersByExceptionIds(exceptionIds: number[]): Promise<ExceptionCciioAnswersModel> {
    return await this.makePost<ExceptionCciioAnswersModel>('/specialtygroupexceptions/cciio-answers', exceptionIds);
  }

  public async bulkUpdateSpecialtyGroupExceptionsCciioAnswers(model: BulkExceptionCciioAnswersModel): Promise<void> {
    await this.makePatch<void>('/specialtygroupexceptions/bulk/cciio-answers', model);
  }

  // OTHER

  /**
  * @param metadataId document metadata ID to be downloaded
  * @returns the URL to download the metadata ID from.
  */
  public downloadSupplementaryDocument(metadataId: number): string {
    return `${window.apiEndpointV2_0}/supplementarydocumentation/${metadataId}/download`;
  }

}